// Dedicated workspace for entering and editing a Current Staff Plan.
//
// Routes:
//   /clients/:clientId/staff-plans/new          → blank plan for clientId
//   /clients/:clientId/staff-plans/:planId      → edit existing
// When opened with ?from=:scenarioId the plan is linked to that scenario on
// the first save, and the scenario's currentStaffByLevel is refreshed.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles, Trash2, Upload, Users } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from '../components/Toast';
import {
  currencySymbol,
  flattenStaffPlanToByLevel,
  isActualStaffPlan,
  modelCurrency,
  staffPlanEntrySource,
  staffPlanEntrySourceLabel,
  staffPlanHorizonWords,
  totalsForStaffPlan,
} from '@rc/engine';
import type {
  Client,
  Model,
  Scenario,
  StaffPlan,
  StaffPlanCell,
  StaffPlanHubLocation,
  StaffPlanSource,
} from '@rc/engine/types';
import StaffPlanGrid from '../components/staffPlan/StaffPlanGrid';
import OtherRolesTable from '../components/staffPlan/OtherRolesTable';
import HubLocationPicker from '../components/staffPlan/HubLocationPicker';
import { basisLabel } from '../components/results/automationBasis';
import type { AuthUser, ModelSummary } from '../lib/api';
import { canDeleteClientContent } from '../lib/nbaScope';
import { parseGeoclientId, withGeoclientQuery } from '../lib/geoclientContext';
import { fmtFte } from '../lib/format';
import { loadImportDraft } from '../lib/staffPlanImport';

function seedCellsFromScenario(scenario: Scenario | null): StaffPlanCell[] {
  if (!scenario?.profile?.currentStaffByLevel) return [];
  const cells: StaffPlanCell[] = [];
  for (const [k, v] of Object.entries(scenario.profile.currentStaffByLevel)) {
    const [disciplineId, levelId] = k.split(':');
    if (!disciplineId || !levelId) continue;
    const fte = Number(v) || 0;
    if (!fte) continue;
    cells.push({
      disciplineId,
      channelId: 'all',
      levelId: levelId as StaffPlanCell['levelId'],
      fte,
    });
  }
  return cells;
}

export default function StaffPlanWorkspace({ user }: { user: AuthUser }) {
  const { clientId, planId } = useParams<{ clientId?: string; planId?: string }>();
  const [search] = useSearchParams();
  const fromScenarioId = search.get('from') || undefined;
  const geoclientId = parseGeoclientId(search.get('geoclientId') || search.get('geoclientId'));
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [modelSummaries, setModelSummaries] = useState<ModelSummary[]>([]);
  const [switchingModel, setSwitchingModel] = useState(false);
  const [plan, setPlan] = useState<StaffPlan | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seedScenario, setSeedScenario] = useState<Scenario | null>(null);

  const isNew = !planId || planId === 'new';
  const canDeleteContent = clientId ? canDeleteClientContent(user, clientId) : false;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const c = await api.getClient(clientId);
        if (cancelled) return;
        setClient(c);

        // Load any existing plan first — its `modelId` wins over everything
        // else when editing.
        let loadedPlan: StaffPlan | null = null;
        if (!isNew && planId) {
          loadedPlan = await api.getStaffPlan(clientId, planId);
        }

        // Resolve the seed scenario referenced by `?from=` (if any).
        let scenarioSource: Scenario | null = null;
        if (fromScenarioId) {
          try {
            scenarioSource = await api.getScenario(clientId, fromScenarioId);
          } catch {
            scenarioSource = null;
          }
        }
        setSeedScenario(scenarioSource);

        // Pick the modelId for this staff plan. Order of preference:
        //   1. The existing plan's saved modelId (for edits).
        //   2. The seed scenario's modelId (when navigated with ?from=).
        //   3. The most-recent scenario for this client.
        //   4. The first model in the catalog as an absolute fallback.
        let resolvedModelId: string | undefined =
          loadedPlan?.modelId || scenarioSource?.modelId;
        if (!resolvedModelId) {
          try {
            const scens = await api.listScenarios(clientId);
            const recent = [...scens].sort((a, b) =>
              (b.updatedAt || '').localeCompare(a.updatedAt || ''),
            )[0];
            if (recent?.modelId) resolvedModelId = recent.modelId;
          } catch {
            // ignore — we'll fall back to the catalog below
          }
        }
        if (!resolvedModelId) {
          const all = await api.listModelSummaries();
          resolvedModelId = all[0]?.id;
        }
        if (!resolvedModelId) {
          throw new Error(
            'No models available — create one in the Models module first.',
          );
        }

        const [m, summaries] = await Promise.all([
          api.getModel(resolvedModelId),
          api.listModelSummaries().catch(() => [] as ModelSummary[]),
        ]);
        if (cancelled) return;
        setModel(m);
        setModelSummaries(summaries);

        if (!loadedPlan) {
          const seedCells = seedCellsFromScenario(scenarioSource);
          loadedPlan = {
            id: crypto.randomUUID(),
            clientId,
            name: `${c.name} — current staff plan`,
            modelId: resolvedModelId,
            kind: 'actual',
            entrySource: 'manual',
            cells: seedCells,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setDirty(true);
        } else {
          setDirty(false);
        }
        if (cancelled) return;
        setPlan(loadedPlan);
      } catch (err) {
        toast('error', (err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, planId, isNew, fromScenarioId]);

  const totals = useMemo(() => totalsForStaffPlan(plan ?? undefined), [plan]);

  // Hub references on the plan that no longer exist in the selected model.
  // A hub with no `hubId` is a shore-only hint and is always considered valid.
  // NOTE: this must stay above the early-return below so the hook always
  // runs on every render (Rules of Hooks) — hoisting it after the guard
  // caused the hook count to differ between the loading and loaded renders,
  // which crashes the component (blank screen).
  const invalidHubs = useMemo(() => {
    const empty = { defaultInvalid: false, rowKeys: [] as string[] };
    if (!plan || !model) return empty;
    const valid = new Set((model.ogsHubs ?? []).map((h) => h.id));
    const rowKeys: string[] = [];
    for (const [key, hub] of Object.entries(plan.rowHubs ?? {})) {
      if (hub?.hubId && !valid.has(hub.hubId)) rowKeys.push(key);
    }
    const defaultInvalid = !!(plan.hubLocation?.hubId && !valid.has(plan.hubLocation.hubId));
    return { defaultInvalid, rowKeys };
  }, [plan, model]);

  if (!clientId) {
    return <div className="px-10 py-8 text-slate-500">Missing client.</div>;
  }
  if (loading || !plan || !model || !client) {
    return <div className="px-10 py-8 text-slate-500">Loading staff plan…</div>;
  }

  const updatePlan = (patch: Partial<StaffPlan>) => {
    setPlan((p) => (p ? { ...p, ...patch } : p));
    setDirty(true);
  };

  const setRowHub = (
    disciplineId: string,
    channelId: string,
    hub: StaffPlanHubLocation | undefined,
  ) => {
    setPlan((p) => {
      if (!p) return p;
      const key = `${disciplineId}::${channelId}`;
      const next = { ...(p.rowHubs || {}) };
      if (hub) next[key] = hub;
      else delete next[key];
      return { ...p, rowHubs: next };
    });
    setDirty(true);
  };

  const handleModelChange = async (nextId: string) => {
    if (!plan || !nextId || nextId === plan.modelId) return;
    setSwitchingModel(true);
    try {
      const m = await api.getModel(nextId);
      setModel(m);
      updatePlan({ modelId: nextId });
    } catch (err) {
      toast('error', `Couldn't switch model: ${(err as Error).message}`);
    } finally {
      setSwitchingModel(false);
    }
  };

  const hasInvalidHubs = invalidHubs.defaultInvalid || invalidHubs.rowKeys.length > 0;

  const resetInvalidHubs = () => {
    setPlan((p) => {
      if (!p || !model) return p;
      const valid = new Set((model.ogsHubs ?? []).map((h) => h.id));
      const nextRowHubs = { ...(p.rowHubs || {}) };
      for (const [key, hub] of Object.entries(nextRowHubs)) {
        if (hub?.hubId && !valid.has(hub.hubId)) delete nextRowHubs[key];
      }
      const nextDefault =
        p.hubLocation?.hubId && !valid.has(p.hubLocation.hubId) ? undefined : p.hubLocation;
      return { ...p, rowHubs: nextRowHubs, hubLocation: nextDefault };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!plan || !client) return;
    try {
      // Grid entry is manual unless this plan was already tagged as uploaded.
      const otherRoles = (plan.otherRoles || [])
        .map((r) => ({ ...r, role: r.role.trim() }))
        .filter((r) => r.role);
      const toSave: StaffPlan = {
        ...plan,
        otherRoles,
        kind: plan.kind || 'actual',
        entrySource:
          isActualStaffPlan(plan) && staffPlanEntrySource(plan) === 'uploaded'
            ? 'uploaded'
            : isActualStaffPlan(plan)
              ? 'manual'
              : plan.entrySource,
      };
      const saved = isNew
        ? await api.createStaffPlan(client.id, toSave, geoclientId)
        : await api.saveStaffPlan(client.id, toSave, geoclientId);
      setPlan(saved);
      setDirty(false);
      toast('success', `Saved "${saved.name}"`);

      // If launched from a scenario, link the plan to that scenario and
      // refresh its currentStaffByLevel so the engine + comparison views pick
      // it up immediately.
      if (seedScenario && (isNew || !seedScenario.profile.currentStaffPlanId)) {
        const updatedScenario: Scenario = {
          ...seedScenario,
          profile: {
            ...seedScenario.profile,
            currentStaffPlanId: saved.id,
            currentStaffByLevel: flattenStaffPlanToByLevel(saved.cells),
            currentStaffByDiscipline: {},
          },
        };
        try {
          await api.saveScenario(client.id, updatedScenario, geoclientId);
        } catch (err) {
          toast('error', `Linked plan saved but scenario update failed: ${(err as Error).message}`);
        }
      }

      if (isNew) {
        navigate(withGeoclientQuery(`/clients/${client.id}/staff-plans/${saved.id}`, geoclientId), {
          replace: true,
        });
      }
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (isNew || !plan || !client) {
      navigate(
        fromScenarioId
          ? withGeoclientQuery(`/clients/${client?.id}/scenarios/${fromScenarioId}`, geoclientId)
          : withGeoclientQuery(`/clients/${client?.id || ''}`, geoclientId),
      );
      return;
    }
    if (!confirm(`Delete staff plan "${plan.name}"?`)) return;
    try {
      await api.deleteStaffPlan(client.id, plan.id, geoclientId);
      toast('info', `Deleted "${plan.name}"`);
      navigate(
        fromScenarioId
          ? withGeoclientQuery(`/clients/${client.id}/scenarios/${fromScenarioId}`, geoclientId)
          : withGeoclientQuery(`/clients/${client.id}`, geoclientId),
      );
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  const backHref = fromScenarioId
    ? withGeoclientQuery(`/clients/${client.id}/scenarios/${fromScenarioId}`, geoclientId)
    : withGeoclientQuery(`/clients/${client.id}`, geoclientId);

  const importHref = withGeoclientQuery(
    `/clients/${clientId}/staff-plans/${planId || 'new'}/import${
      fromScenarioId ? `?from=${fromScenarioId}` : ''
    }`,
    geoclientId,
  );
  const rosterHref = withGeoclientQuery(
    `/clients/${clientId}/staff-plans/${planId || 'new'}/import?step=review${
      fromScenarioId ? `&from=${fromScenarioId}` : ''
    }`,
    geoclientId,
  );
  const importDraft =
    clientId && planId ? loadImportDraft(clientId, planId) : null;
  const hasRoster = !!(importDraft && importDraft.rows.length > 0);
  const uploadedWithoutRoster =
    isActualStaffPlan(plan) &&
    staffPlanEntrySource(plan) === 'uploaded' &&
    !hasRoster &&
    !isNew;

  return (
    <div className="flex flex-col min-h-full">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={backHref} className="text-slate-400 hover:text-slate-700 shrink-0" title="Back">
              <ArrowLeft size={18} />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-brand-purple-500" />
                <h1 className="text-lg font-semibold truncate">{planHeading(plan)}</h1>
                {dirty && <span className="pill-purple">unsaved</span>}
                {isNew && <span className="pill bg-slate-100 text-slate-600">new</span>}
                {isActualStaffPlan(plan) && (
                  <span
                    className={
                      staffPlanEntrySource(plan) === 'uploaded'
                        ? 'pill bg-sky-50 text-sky-800'
                        : 'pill bg-slate-100 text-slate-600'
                    }
                    title={
                      staffPlanEntrySource(plan) === 'uploaded'
                        ? 'Entered via the staff plan upload wizard'
                        : 'Entered or edited directly in the staff plan grid'
                    }
                  >
                    {staffPlanEntrySourceLabel(plan)}
                  </span>
                )}
                {/* Provenance is fixed at creation, so editing this plan will not
                    turn it into the client's actual team. Worth saying here
                    rather than letting a save quietly not do what was expected. */}
                {!isActualStaffPlan(plan) &&
                  (plan.kind === 'final' ? (
                    <span
                      className="pill bg-emerald-50 text-emerald-700"
                      title={`Post-automation headcount at ${staffPlanHorizonWords(
                        plan.source?.horizon,
                      )}, generated from a scenario. It is the end of that chain: it cannot be attached to a scenario as its actual team, which is what stops automation being released from it twice.`}
                    >
                      Final · {staffPlanHorizonWords(plan.source?.horizon)}
                    </span>
                  ) : (
                    <span
                      className="pill bg-brand-purple-50 text-brand-purple-700"
                      title="Generated from a scenario's modeled output. It stays a record of that plan and cannot be attached to a scenario as its actual team."
                    >
                      generated from a model
                    </span>
                  ))}
              </div>
              <div className="text-xs text-slate-500 truncate">
                Client: <span className="text-slate-700">{client.name}</span>
                {' · '}
                Model: <span className="text-slate-700">{model.name}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasRoster && (
              <Link
                to={rosterHref}
                className="btn-secondary"
                title="Open the person-level roster; saving again refreshes the matrix"
              >
                <Users size={14} /> Edit roster
              </Link>
            )}
            <Link to={importHref} className="btn-secondary" title="Upload Excel staff plan">
              <Upload size={14} /> Upload staff plan
            </Link>
            {(isNew || canDeleteContent) && (
              <button onClick={handleDelete} className="btn-secondary" title={isNew ? 'Discard' : 'Delete'}>
                <Trash2 size={14} /> {isNew ? 'Discard' : 'Delete'}
              </button>
            )}
            <button onClick={handleSave} className="btn-primary" disabled={!dirty}>
              <Save size={14} /> {dirty ? 'Save plan' : 'Saved'}
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 px-8 py-6 w-full space-y-4">
        {hasRoster && importDraft!.status === 'draft' && (
          <div className="rounded-lg border border-brand-purple-200 bg-brand-purple-50/50 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0 text-sm text-brand-purple-900">
              <span className="font-medium">Import draft in progress</span>
              {importDraft!.fileName ? ` — ${importDraft!.fileName}` : ''}
              <span className="text-brand-purple-700">
                {' '}
                ({importDraft!.rows.length} people, step: {importDraft!.step})
              </span>
            </div>
            <Link to={importHref} className="btn-secondary shrink-0">
              Resume upload
            </Link>
          </div>
        )}
        {hasRoster && importDraft!.status === 'saved' && (
          <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0 text-sm text-sky-950">
              <span className="font-medium">Person roster available</span>
              {importDraft!.fileName ? ` — ${importDraft!.fileName}` : ''}
              <span className="text-sky-800">
                {' '}
                ({importDraft!.rows.length} people). Adjust or add people, then save to refresh the
                matrix.
              </span>
            </div>
            <Link to={rosterHref} className="btn-secondary shrink-0">
              <Users size={14} /> Edit roster
            </Link>
          </div>
        )}
        {uploadedWithoutRoster && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-950">
            <span className="font-medium">Uploaded plan</span>
            {' — '}
            the person-level roster isn&apos;t available in this browser (rosters stay local after
            import). Re-upload to rebuild it, or edit the matrix directly below.
          </div>
        )}
        <div className="card">
          <div className="card-body grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 space-y-3">
              <div>
                <label className="label">Plan name</label>
                <input
                  className="input text-base font-medium"
                  value={plan.name}
                  onChange={(e) => updatePlan({ name: e.target.value })}
                />
                <div className="text-[11px] font-mono text-slate-400 mt-1">id: {plan.id}</div>
              </div>
              <div>
                <label className="label">Model</label>
                <select
                  className="input text-sm"
                  value={plan.modelId}
                  onChange={(e) => handleModelChange(e.target.value)}
                  disabled={switchingModel}
                >
                  {modelSummaries.length === 0 && (
                    <option value={plan.modelId}>{model.name}</option>
                  )}
                  {modelSummaries.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Determines the available channels, levels and hub locations.
                  Pick the model the scenario(s) using this plan will run on so
                  hub selections stay valid.
                </p>
              </div>
              <HubLocationPicker
                model={model}
                value={plan.hubLocation}
                onChange={(hubLocation) => updatePlan({ hubLocation })}
              />
              <div>
                <label className="label">Notes</label>
                <textarea
                  className="input text-sm leading-relaxed"
                  value={plan.notes || ''}
                  onChange={(e) => updatePlan({ notes: e.target.value })}
                  rows={2}
                  placeholder="Snapshot date, source workbook, any caveats…"
                />
              </div>
              {seedScenario && (
                <div className="text-xs text-slate-500">
                  Will be linked to scenario{' '}
                  <span className="font-medium text-slate-700">{seedScenario.name}</span> on save.
                </div>
              )}
            </div>
            <div className="lg:col-span-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total FTE" value={totals.total.toFixed(1)} accent />
                <Stat label="Onshore" value={totals.onTotal.toFixed(1)} />
                <Stat label="Hub" value={totals.hubTotal.toFixed(1)} hub />
              </div>
              <div className={totals.otherTotal > 0 ? 'grid grid-cols-3 gap-3' : 'grid grid-cols-2 gap-3'}>
                <Stat
                  label="Disciplines"
                  value={Object.values(totals.byDiscipline).filter((v) => v > 0).length}
                />
                <Stat label="Cells" value={plan.cells.length} />
                {totals.otherTotal > 0 && (
                  <Stat label="Other (not modeled)" value={totals.otherTotal.toFixed(1)} />
                )}
              </div>
              {plan.updatedAt && (
                <div className="text-[11px] text-slate-400">
                  Last updated {new Date(plan.updatedAt).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {plan.kind === 'final' && plan.source && (
          <ScenarioProvenanceCard
            clientId={client.id}
            model={model}
            source={plan.source}
            editedAt={
              plan.updatedAt && plan.updatedAt > plan.source.generatedAt
                ? plan.updatedAt
                : undefined
            }
          />
        )}

        {hasInvalidHubs && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-amber-900">
                Some hub selections aren't available in "{model.name}"
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {invalidHubs.rowKeys.length > 0 && (
                  <>
                    {invalidHubs.rowKeys.length} row hub
                    {invalidHubs.rowKeys.length === 1 ? '' : 's'}
                    {invalidHubs.defaultInvalid ? ' and the default hub' : ''}
                  </>
                )}
                {invalidHubs.rowKeys.length === 0 && invalidHubs.defaultInvalid && (
                  <>The default hub</>
                )}{' '}
                reference a hub that no longer exists in this model. Until reset,
                those rows fall back to the model's default shore rate when
                costing.
              </div>
            </div>
            <button onClick={resetInvalidHubs} className="btn-secondary shrink-0">
              Reset invalid hubs
            </button>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <div>
              <h3 className="text-sm font-semibold">Staff plan — Discipline × Channel × Level</h3>
              <p className="text-xs text-slate-500">
                Enter the team you have today. Each level splits into{' '}
                <span className="font-medium text-slate-700">On</span> (onshore) and{' '}
                <span className="font-medium text-amber-800">Hub</span> (offshore + nearshore).
                Pick each row's Hub location in the grid — rows left on "Default" use the default
                hub selected above. Use "All channels" rows for disciplines that aren't
                channel-specific (Strategy, Planning, Analytics, etc.).
              </p>
            </div>
          </div>
          <div className="card-body">
            <StaffPlanGrid
              model={model}
              cells={plan.cells}
              onChange={(cells) => updatePlan({ cells })}
              rowHubs={plan.rowHubs}
              onRowHubChange={setRowHub}
              hubLabel={
                plan.hubLocation
                  ? `${plan.hubLocation.label || 'Hub'} (${plan.hubLocation.shore})`
                  : undefined
              }
            />
            {(isActualStaffPlan(plan) || (plan.otherRoles?.length ?? 0) > 0) && (
              <div className="mt-4">
                <OtherRolesTable
                  roles={plan.otherRoles || []}
                  onChange={
                    isActualStaffPlan(plan)
                      ? (otherRoles) => updatePlan({ otherRoles })
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** What this screen is editing. A generated plan opened here is not the client's
 *  current team, and calling it one has misled people into saving it as such. */
function planHeading(plan: StaffPlan): string {
  if (plan.kind === 'final') return 'Final Staff Plan';
  if (plan.kind === 'modeled') return 'Modeled Staff Plan';
  return 'Current Staff Plan';
}

/**
 * The scenario that produced a Final plan, as it stood when the plan was saved.
 *
 * Read from the snapshot frozen onto the plan rather than from the live scenario:
 * scenarios keep being edited, and inputs that no longer produced these numbers
 * would explain nothing. The link out is for going to the scenario as it is now,
 * which is a different question from what this plan came from.
 */
function ScenarioProvenanceCard({
  clientId,
  model,
  source,
  editedAt,
}: {
  clientId: string;
  model: Model;
  source: StaffPlanSource;
  editedAt?: string;
}) {
  const snapshot = source.snapshot;
  const profile = snapshot?.profile;
  const symbol = currencySymbol(modelCurrency(model).base);
  const automations = snapshot?.automations || [];
  const markets = profile?.scenarioMarkets || [];

  return (
    <div className="card">
      <div className="card-header">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
            Generated from a scenario
          </div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Sparkles size={14} className="text-emerald-600" />
            {source.scenarioName || 'Scenario'} — staffing after automation at{' '}
            {staffPlanHorizonWords(source.horizon)}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Saved {new Date(source.generatedAt).toLocaleString()}
            {snapshot?.modelName && (
              <>
                {' · '}
                {snapshot.modelName}
                {snapshot.modelVersion ? ` v${snapshot.modelVersion}` : ''}
              </>
            )}
          </p>
        </div>
        <Link
          to={`/clients/${clientId}/scenarios/${source.scenarioId}`}
          className="btn-secondary text-xs shrink-0"
          title="Open the scenario as it stands now"
        >
          Open scenario
        </Link>
      </div>
      <div className="card-body space-y-3">
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-[11px]">
          <Fact
            label="Before automation"
            value={snapshot?.beforeFte != null ? `${fmtFte(snapshot.beforeFte)} FTE` : '—'}
          />
          <Fact
            label="Released"
            value={source.releasedFte != null ? `${fmtFte(source.releasedFte)} FTE` : '—'}
          />
          <Fact
            label="After automation"
            value={snapshot?.afterFte != null ? `${fmtFte(snapshot.afterFte)} FTE` : '—'}
          />
          <Fact
            label="Cells automation touched"
            value={source.automationCellCount != null ? String(source.automationCellCount) : '—'}
          />
          <Fact label="Released from" value={source.basis ? basisLabel(source.basis) : '—'} />
          <Fact
            label="Budget"
            value={
              profile?.totalBudget
                ? `${symbol}${Math.round(profile.totalBudget).toLocaleString()}`
                : '—'
            }
          />
          <Fact
            label="Markets"
            value={markets.length ? markets.join(', ') : '—'}
          />
          <Fact
            label="Hand adjustments"
            value={
              source.adjustedCellCount
                ? `${source.adjustedCellCount} cell${source.adjustedCellCount === 1 ? '' : 's'}`
                : 'None'
            }
          />
        </dl>

        {automations.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Automations applied
            </div>
            <div className="mt-1.5 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th className="font-medium py-1 pr-3">Automation</th>
                    <th className="font-medium py-1 pr-3">Starts</th>
                    <th className="font-medium py-1 pr-3">Ramp</th>
                    <th className="font-medium py-1 pr-3">Landed</th>
                    <th className="font-medium py-1 text-right">Released</th>
                  </tr>
                </thead>
                <tbody>
                  {automations.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="py-1 pr-3 text-slate-800">{a.name}</td>
                      <td className="py-1 pr-3 text-slate-600 tabular-nums">
                        month {a.startMonth}
                      </td>
                      <td className="py-1 pr-3 text-slate-600 tabular-nums">
                        {a.rampMonths} mo
                      </td>
                      <td className="py-1 pr-3 text-slate-600 tabular-nums">
                        {(a.adoption * 100).toFixed(0)}%
                      </td>
                      <td className="py-1 text-right text-slate-800 tabular-nums">
                        {fmtFte(a.releasedFte, 2)} FTE
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!snapshot && (
          <p className="text-[11px] text-slate-500">
            This plan was saved before the scenario inputs were recorded, so only
            the headline figures above are known.
          </p>
        )}

        {editedAt && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
            The cells were edited by hand on{' '}
            {new Date(editedAt).toLocaleString()}, after this plan was generated.
            The figures above describe what the scenario produced, not what is in
            the grid now.
          </p>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 font-medium truncate" title={value}>
        {value}
      </dd>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  hub,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
  hub?: boolean;
}) {
  const containerClass = accent
    ? 'border-brand-purple-200 bg-brand-purple-50'
    : hub
      ? 'border-amber-200 bg-amber-50/60'
      : 'border-slate-200 bg-white';
  const labelClass = hub ? 'text-amber-700' : 'text-slate-500';
  const valueClass = accent
    ? 'text-brand-purple-700'
    : hub
      ? 'text-amber-900'
      : 'text-slate-800';
  return (
    <div className={'rounded-lg border px-3 py-2 text-center ' + containerClass}>
      <div className={'text-[11px] uppercase tracking-wider ' + labelClass}>{label}</div>
      <div className={'text-lg font-semibold tabular-nums ' + valueClass}>{value}</div>
    </div>
  );
}
